import { Icon } from '@iconify/react'
import Modal from '../common/Modal'
import { useState } from 'react'

const SiteInfoModal = ({ siteInfo }) => {
  if (!siteInfo) return null

  const InfoCard = ({ icon, label, value, colorClass = 'text-primary bg-primary/5 border-primary/10' }) => (
    <div className={`flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-all hover:shadow-md`}>
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass.split(' ').filter(c => c.startsWith('bg-') || c.startsWith('text-')).join(' ')}`}>
        <Icon icon={icon} className="text-2xl" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-sm font-semibold text-gray-800 truncate leading-tight">{value || 'N/A'}</p>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <InfoCard icon="mdi:web" label="Site Name" value={siteInfo.name} colorClass="text-blue-600 bg-blue-50" />
        <InfoCard icon="mdi:link-box-variant-outline" label="Site URL" value={siteInfo.url} colorClass="text-primary bg-primary/5" />
        <InfoCard icon="mdi:wordpress" label="WordPress Core" value={siteInfo.wordpress_version} colorClass="text-sky-600 bg-sky-50" />
        <InfoCard icon="mdi:shield-check-outline" label="Connect Plugin" value={siteInfo.plugin_version} colorClass="text-green-600 bg-green-50" />
        <InfoCard icon="mdi:brush-outline" label="Active Theme" value={`${siteInfo.theme?.name} ${siteInfo.theme?.version}`} colorClass="text-orange-600 bg-orange-50" />
        <InfoCard icon="mdi:file-multiple-outline" label="Content Pages" value={siteInfo.total_pages} colorClass="text-purple-600 bg-purple-50" />
        <InfoCard icon="mdi:earth" label="Server Timezone" value={siteInfo.timezone} colorClass="text-gray-600 bg-gray-50" />
        <InfoCard icon="mdi:text-box-outline" label="Description" value={siteInfo.description || 'No description provided'} colorClass="text-slate-600 bg-slate-50" />
      </div>
    </div>
  )
}

const PagesModal = ({ pages }) => {
  if (!pages || pages.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-sm">
          <Icon icon="mdi:file-search-outline" className="text-3xl text-gray-300" />
        </div>
        <h3 className="text-md font-semibold text-gray-800">No pages discovered</h3>
        <p className="text-gray-500 text-xs mt-1">We couldn't retrieve the list of public pages from your WordPress site.</p>
      </div>
    )
  }

  return (
    <div className="space-y-3 p-1">
      {pages.map((page, index) => (
        <div key={page.id} className="group p-4 bg-white rounded-lg border border-gray-200 hover:border-primary/50 hover:shadow-md transition-all">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold text-white bg-gray-800 px-1.5 py-0.5 rounded tracking-tighter">#{index + 1}</span>
                <h4 className="font-semibold text-gray-800 truncate group-hover:text-primary transition-colors text-sm">
                  {page.title || 'Untitled Page'}
                </h4>
              </div>
              <p className="text-[11px] text-gray-500 flex items-center gap-1.5 mb-3 font-medium bg-gray-50 p-2 rounded border border-gray-100/50">
                <Icon icon="mdi:link-variant" className="text-primary text-xs" />
                <a href={page.url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">
                  {page.url}
                </a>
              </p>
              <div className="flex flex-wrap items-center gap-3 text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                <div className="flex items-center gap-1">
                  <Icon icon="mdi:fingerprint" />
                  ID: {page.id}
                </div>
                <div className="flex items-center gap-1">
                  <Icon icon="mdi:clock-check-outline" />
                  Updated: {page.modified}
                </div>
                {page.page_builder && (
                  <div className="flex items-center gap-1 text-primary">
                    <Icon icon="mdi:lightning-bolt" />
                    Builder: {page.page_builder}
                  </div>
                )}
              </div>
            </div>
            <div className={`shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase border shadow-sm ${page.has_fixes ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-400 border-gray-200'
              }`}>
              <Icon icon={page.has_fixes ? 'mdi:check-decagram' : 'mdi:circle-off-outline'} className="text-xs" />
              {page.has_fixes ? 'Fixes Ready' : 'N/A'}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

const ScanHistoryModal = ({ history }) => {
  const historyArray = Array.isArray(history) ? history : history?.history || []

  if (!historyArray || historyArray.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-sm">
          <Icon icon="mdi:history" className="text-3xl text-gray-300" />
        </div>
        <h3 className="text-md font-semibold text-gray-800">No scan records</h3>
        <p className="text-gray-500 text-xs mt-1">You haven't performed any accessibility scans on this site yet.</p>
      </div>
    )
  }

  const getScoreColor = (score) => {
    if (score >= 90) return "text-green-600 bg-green-50 border-green-200";
    if (score >= 70) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between mb-4 px-1">
        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
          <Icon icon="mdi:format-list-bulleted" />
          Execution Log
        </h3>
        <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full uppercase border border-gray-200">
          {historyArray.length} Records
        </span>
      </div>

      <div className="divide-y divide-gray-100 bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        {historyArray.map((scan, index) => {
          const scanResults = scan.scan_results || scan
          const violations = scan.violation_count || scanResults.violation_count || (scanResults.violations?.length) || 0
          const score = Math.max(0, 100 - (violations * 5))

          return (
            <div key={scan.id || index} className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 hover:bg-gray-50 transition-colors group">
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center border shadow-sm ${violations > 0 ? "bg-red-50 text-red-500 border-red-100" : "bg-green-50 text-green-500 border-green-100"
                    }`}>
                    <Icon icon={violations > 0 ? "mdi:alert-circle-outline" : "mdi:check-decagram-outline"} className="text-xl" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-semibold text-gray-800 truncate group-hover:text-primary transition-colors text-sm mb-0.5">
                      {scan.page_url || scanResults.url || 'Internal Audit'}
                    </h4>
                    <div className="flex items-center gap-3">
                      <p className="text-[10px] text-gray-400 flex items-center gap-1 font-bold uppercase">
                        <Icon icon="mdi:calendar-clock" />
                        {formatDate(scan.scan_date || scanResults.scan_date)}
                      </p>
                      {scanResults.wcag_level && (
                        <span className="text-[10px] font-bold text-primary uppercase">
                          • {scanResults.wcag_level.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-3 ml-13 sm:ml-0">
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-gray-100 rounded text-gray-600 border border-gray-200/50">
                    <Icon icon="mdi:alert-octagon" className={violations > 0 ? "text-red-500" : "text-green-500"} />
                    {violations} Issues
                  </span>
                  {scanResults.scanner && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-gray-100 rounded text-gray-400 border border-gray-200/50">
                      <Icon icon="mdi:robot-outline" />
                      {scanResults.scanner}
                    </span>
                  )}
                </div>
              </div>

              <div className={`px-4 py-2 rounded-lg border text-center flex items-center gap-4 shadow-sm min-w-[110px] h-fit ${getScoreColor(score)}`}>
                <div className="text-lg font-bold">{score}%</div>
                <div className="text-[10px] font-bold uppercase tracking-tight opacity-70">Score</div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const ViolationItem = ({ violation }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const impact = violation.impact?.toLowerCase() || 'minor'

  const getStatusIcon = () => {
    if (impact === 'critical' || impact === 'serious') {
      return (
        <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <Icon icon="mdi:close" className="text-red-600 text-sm" />
        </div>
      )
    }
    return (
      <div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
        <Icon icon="mdi:alert" className="text-yellow-600 text-sm" />
      </div>
    )
  }

  return (
    <div className="border-b border-gray-200 last:border-b-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 py-3 px-4 hover:bg-gray-50 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        {getStatusIcon()}
        <span className="flex-1 text-sm text-gray-800 font-medium">
          {violation.help || violation.id}
        </span>
        <div className="flex items-center gap-3">
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${impact === 'critical' ? 'bg-red-100 text-red-700' :
              impact === 'serious' ? 'bg-orange-100 text-orange-700' :
                'bg-yellow-100 text-yellow-700'
            }`}>
            {impact}
          </span>
          <Icon
            icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
            className="text-gray-400 text-xl flex-shrink-0"
          />
        </div>
      </button>

      {isExpanded && (
        <div className="bg-gray-50 border-t border-gray-200 p-4">
          <div className="space-y-4">
            <div>
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                <Icon icon="mdi:information-outline" />
                Requirement
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed font-medium">{violation.description}</p>
            </div>

            {violation.nodes?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                  <Icon icon="mdi:code-tags" />
                  Technical Evidence
                </h4>
                <div className="space-y-3">
                  {violation.nodes.map((node, nodeIdx) => (
                    <div key={nodeIdx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm relative">
                      <span className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-gray-800 text-white text-[9px] font-bold flex items-center justify-center border-2 border-white shadow-sm">
                        {nodeIdx + 1}
                      </span>
                      <div className="space-y-2">
                        <div>
                          <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Source Snippet</p>
                          <code className="block text-xs bg-gray-900 text-gray-100 p-2.5 rounded-lg overflow-x-auto font-mono border border-gray-800">
                            {node.html}
                          </code>
                        </div>
                        {node.target && (
                          <div>
                            <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">DOM Path</p>
                            <code className="block text-[9px] bg-gray-100 text-gray-500 p-1.5 rounded font-mono border border-gray-200/50 break-all">
                              {Array.isArray(node.target) ? node.target.join(' > ') : node.target}
                            </code>
                          </div>
                        )}
                        {node.failureSummary && (
                          <div className="p-2 bg-red-50 border border-red-100 rounded text-xs text-red-700 italic">
                            <strong>Issue Detail:</strong> {node.failureSummary.replace('Fix any of the following:', '').replace('Fix all of the following:', '').trim()}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {violation.helpUrl && (
              <a
                href={violation.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline text-xs font-bold flex items-center justify-center w-full p-2 bg-white border border-gray-200/50 rounded-lg shadow-sm gap-1"
              >
                Learn more about this requirement
                <Icon icon="mdi:open-in-new" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const ViolationsModal = ({ violationsData }) => {
  if (!violationsData || !Array.isArray(violationsData) || violationsData.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
        <Icon icon="mdi:check-circle" className="text-5xl text-green-500/20 mx-auto mb-3" />
        <h3 className="text-md font-semibold text-gray-800">No violations found</h3>
        <p className="text-gray-500 text-xs mt-1">Your site appears to be following accessibility best practices.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-1">
      {violationsData.map((record, recordIndex) => (
        <div key={record.id || recordIndex} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-[10px] font-bold rounded uppercase border border-gray-300">Scan ID: {record.id}</span>
                  <h3 className="font-bold text-gray-800 truncate text-sm">{record.page_url || 'Main Content Area'}</h3>
                </div>
                <p className="text-[10px] text-gray-400 flex items-center gap-1 font-bold">
                  <Icon icon="mdi:clock-outline" />
                  DATE: {new Date(record.scan_date).toLocaleString()}
                </p>
              </div>
              <div className="flex gap-2">
                <div className="text-center px-4 py-1.5 bg-white border border-gray-200 rounded-lg flex items-center gap-3">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">Issues</span>
                  <span className={`text-lg font-black ${parseInt(record.violation_count) > 0 ? 'text-danger' : 'text-success'}`}>{record.violation_count}</span>
                </div>
                {record.wcag_level && (
                  <div className="text-center px-4 py-1.5 bg-white border border-gray-200 rounded-lg flex items-center gap-3">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">Level</span>
                    <span className="text-lg font-black text-primary">{record.wcag_level.toUpperCase()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {record.violations?.length > 0 ? (
              record.violations.map((violation, vIdx) => (
                <ViolationItem key={vIdx} violation={violation} />
              ))
            ) : (
              <div className="p-10 text-center">
                <Icon icon="mdi:shield-check" className="text-4xl text-green-500/20 mx-auto mb-2" />
                <p className="text-xs text-green-600 font-bold">Zero violations detected for this audit.</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const HTMLViewer = ({ htmlContent }) => {
  if (!htmlContent) return <p className="text-gray-500 text-center py-8">No content available</p>

  const isHTML = typeof htmlContent === 'string' && (htmlContent.trim().startsWith('<!DOCTYPE') || htmlContent.trim().startsWith('<html'))

  if (isHTML) {
    return (
      <div className="space-y-4">
        <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
          <p className="text-xs text-blue-700 font-medium flex items-center gap-2">
            <Icon icon="mdi:information" className="text-lg" />
            Live Preview: This content is being served directly from your WordPress integration.
          </p>
        </div>
        <div className="border border-gray-200 rounded-xl overflow-hidden shadow-inner bg-white">
          <iframe
            srcDoc={htmlContent}
            title="WordPress Content"
            className="w-full h-[550px] border-0"
            sandbox="allow-same-origin allow-scripts"
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => {
              const blob = new Blob([htmlContent], { type: 'text/html' })
              const url = URL.createObjectURL(blob)
              window.open(url, '_blank')
            }}
            className="px-4 py-2 bg-white text-gray-700 border border-gray-200 shadow-sm font-bold text-xs uppercase tracking-widest rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <Icon icon="mdi:open-in-new" className="text-lg" />
            Launch Tab
          </button>
          <button
            onClick={() => {
              const blob = new Blob([htmlContent], { type: 'text/html' })
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `wp-render-${Date.now()}.html`
              a.click()
            }}
            className="px-4 py-2 bg-primary text-white font-bold text-xs uppercase tracking-widest rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 shadow-lg shadow-primary/20"
          >
            <Icon icon="mdi:download" className="text-lg" />
            Save HTML
          </button>
        </div>
      </div>
    )
  }

  try {
    const parsed = typeof htmlContent === 'string' ? JSON.parse(htmlContent) : htmlContent
    return (
      <div className="space-y-3">
        <pre className="text-xs bg-gray-900 text-gray-100 p-5 rounded-xl overflow-auto max-h-96 border border-gray-800 font-mono shadow-xl">
          {JSON.stringify(parsed, null, 2)}
        </pre>
      </div>
    )
  } catch {
    return (
      <div className="space-y-3">
        <pre className="text-xs bg-gray-900 text-gray-100 p-5 rounded-xl overflow-auto max-h-96 whitespace-pre-wrap border border-gray-800 font-mono shadow-xl">
          {htmlContent}
        </pre>
      </div>
    )
  }
}

const AppliedFixesModal = ({ fixes }) => {
  if (!fixes || !Array.isArray(fixes) || fixes.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
        <Icon icon="mdi:auto-fix" className="text-5xl text-gray-200 mx-auto mb-3" />
        <h3 className="text-md font-semibold text-gray-800">No fixes recorded</h3>
        <p className="text-gray-500 text-xs mt-1">We couldn't find any recorded accessibility fixes for this site.</p>
      </div>
    )
  }

  return (
    <div className="space-y-3 p-1">
      {fixes.map((fix, index) => (
        <div key={index} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-600 border border-green-100">
              <Icon icon="mdi:check-decagram" className="text-xl" />
            </div>
            <div>
              <h4 className="font-bold text-gray-800 text-sm leading-tight">{fix.type || 'Fix Applied'}</h4>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                {new Date(fix.applied_at || fix.date || Date.now()).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100/50">
            <p className="text-xs text-gray-600 mb-2 font-medium leading-relaxed">{fix.description || 'Improvement successfully deployed.'}</p>
            {fix.page_url && (
              <p className="text-[10px] text-primary flex items-center gap-1.5 truncate font-bold uppercase bg-white w-fit px-2 py-1 rounded border border-gray-200/50">
                <Icon icon="mdi:link-variant" />
                {fix.page_url}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const AccessibilityReportModal = ({ report }) => {
  if (!report) return null

  const stats = [
    { label: 'Audits', value: report.total_scans || 0, icon: 'mdi:magnify', color: 'blue' },
    { label: 'Issues', value: report.total_violations || 0, icon: 'mdi:alert-octagon', color: 'red' },
    { label: 'Passes', value: report.total_passes || 0, icon: 'mdi:check-circle', color: 'green' },
    { label: 'Score', value: `${report.average_score || 0}%`, icon: 'mdi:shield-check', color: 'indigo' }
  ]

  return (
    <div className="space-y-6 p-1">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center">
            <div className={`w-9 h-9 rounded-lg bg-${stat.color}-50 text-${stat.color}-600 border border-${stat.color}-100 flex items-center justify-center mx-auto mb-2`}>
              <Icon icon={stat.icon} className="text-lg" />
            </div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">{stat.label}</p>
            <p className="text-lg font-black text-gray-800 leading-none">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
        <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2 uppercase tracking-wide">
          <Icon icon="mdi:chart-bar" className="text-primary" />
          Analytics Summary
        </h3>
        <div className="space-y-5">
          <div className="flex flex-col gap-2.5">
            <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-widest text-gray-400">
              <span>Overall Integrity</span>
              <span className="text-primary font-black text-xs">{report.average_score || 0}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-200/50">
              <div
                className="h-full bg-primary rounded-full shadow-sm transition-all duration-1000"
                style={{ width: `${report.average_score || 0}%` }}
              ></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="p-4 bg-red-50 rounded-xl border border-red-100/50">
              <h4 className="text-[10px] font-bold text-red-700 uppercase mb-2.5 flex items-center gap-1.5">
                <Icon icon="mdi:trending-down" />
                Critical Backlog
              </h4>
              <p className="text-xl font-black text-gray-800 mb-0.5">{report.critical_violations || 0}</p>
              <p className="text-[9px] text-red-600/60 font-medium">Blocking high-priority compliance components</p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl border border-green-100/50">
              <h4 className="text-[10px] font-bold text-green-700 uppercase mb-2.5 flex items-center gap-1.5">
                <Icon icon="mdi:trending-up" />
                Optimization
              </h4>
              <p className="text-xl font-black text-gray-800 mb-0.5">+{report.improvement_rate || 0}%</p>
              <p className="text-[9px] text-green-600/60 font-medium">Progress compared to baseline measurements</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const WordPressModals = ({ isOpen, onClose, title, content, contentType, size = 'xl' }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size={size}>
      <div className="py-2">
        {content}
      </div>
    </Modal>
  )
}

export {
  SiteInfoModal,
  PagesModal,
  ScanHistoryModal,
  ViolationsModal,
  AppliedFixesModal,
  AccessibilityReportModal,
  HTMLViewer
}
export default WordPressModals
